import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getDataClient } from "@/lib/dataClient";
import { roleHomePath } from "@/hooks/useAuth";
import type { CoachRole } from "@/mocks/aiCoach";

const roleLabels: Record<string, string> = {
  ogrenci: "Öğrenci Paneli",
  veli: "Veli Paneli",
  ogretmen: "Öğretmen Paneli",
  admin: "Yönetici Paneli",
};

const roleIcons: Record<string, string> = {
  ogrenci: "ri-graduation-cap-line",
  veli: "ri-user-heart-line",
  ogretmen: "ri-presentation-line",
  admin: "ri-settings-3-line",
};

interface UserMenuProps {
  name: string;
  role: CoachRole;
}

// Giriş yapmış kullanıcı için kişisel menü: isim, rol paneline kısayol ve çıkış.
export default function UserMenu({ name, role }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const label = roleLabels[role] ?? "Hesabım";
  const icon = roleIcons[role] ?? "ri-user-3-line";
  const panelPath = roleHomePath(role);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleLogout = async () => {
    setOpen(false);
    const dataClient = getDataClient();
    await dataClient.auth.signOut();
    navigate("/", { replace: true });
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2.5 h-11 px-2 sm:px-3 rounded-md border border-background-300 hover:border-primary-300 transition-colors"
      >
        <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
          <i className={`${icon} text-base`} aria-hidden="true"></i>
        </span>
        <span className="hidden sm:flex flex-col items-start leading-tight">
          <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
            Hoşgeldin, {name}
          </span>
          <span className="text-xs text-foreground-500 whitespace-nowrap">{label}</span>
        </span>
        <i
          className="hidden sm:inline ri-arrow-down-s-line text-base text-foreground-400"
          aria-hidden="true"
        ></i>
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-60 z-50">
          <div
            role="menu"
            aria-label="Hesap menüsü"
            className="rounded-lg border border-background-200 bg-background-50 p-1.5"
          >
            <Link
              to={panelPath}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between gap-3 rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <span className="whitespace-nowrap">{label}</span>
              <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex items-center justify-between gap-3 w-full rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <span className="whitespace-nowrap">Çıkış Yap</span>
              <i className="ri-logout-box-r-line text-foreground-400" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}